import { Box, Stack } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Button } from '@portals/core/src/components/Button/Button';
import { CategoryLinkList } from '@portals/core/src/components/CategoryLinkList/CategoryLinkList';
import { Divider } from '@portals/core/src/components/Divider/Divider';
import { Grid } from '@portals/core/src/components/Grid/Grid';
import { Spacer } from '@portals/core/src/components/Spacer/Spacer';
import { Typography } from '@portals/core/src/components/Typography/Typography';
import { ChevronLeft, Target } from '@portals/icons';
import { useField } from 'formik';
import i18next from 'i18next';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { TextInput } from '../../components/TextInput/TextInput';
import { useBankFromGeoIP } from '../../hooks/useBankFromGeoIP/useBankFromGeoIP';
import { useBankFromGeolocation } from '../../hooks/useBankFromGeolocation/useBankFromGeolocation';
import { useBanks } from '../../hooks/useBanks/useBanks';
import { OnSelectBankFn, SparkasseAssignBankButton } from './components/SparkasseAssignBankButton';
import { getStyles } from './SparkasseAssignmentForm.styles';

// eslint-disable-next-line import/no-named-as-default-member
const t = i18next.getFixedT.bind(i18next)(null, 'forms', 'bankCodeAssignment');

export interface SparkasseAssignmentFormProps {
  headline?: string;
  subline?: string;
  onSelectBank?: OnSelectBankFn;
  onLocationChange?(value: 'geo' | 'manually'): void;
  onBack?: () => void;
  guesses?: number;
  suggests?: number;
}

export const SparkasseAssignmentForm: React.FunctionComponent<SparkasseAssignmentFormProps> = ({
  headline,
  subline,
  onSelectBank,
  onLocationChange,
  onBack,
  guesses = 0,
  suggests = 0,
}) => {
  const [{ value: locationValue }, { error: locationError }] = useField('locationName');
  const { banks: locationBanks, getBanks: getLocationBanks } = useBanks();
  const { banks: geoLocationBanks, getBanks: getGeolocationBanks } = useBankFromGeolocation();
  const { banks: geoIPBanks } = useBankFromGeoIP();

  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState<'locationView' | 'geoLocationView'>('locationView');

  const styles = getStyles();

  const banks = useMemo(() => {
    if (view === 'locationView' && locationValue && !locationError) {
      if (onLocationChange) {
        onLocationChange('manually');
      }

      return suggests ? locationBanks.slice(0, suggests) : locationBanks;
    }

    if (view === 'geoLocationView') {
      if (onLocationChange) {
        onLocationChange('geo');
      }

      return guesses ? geoLocationBanks.slice(0, guesses) : geoLocationBanks;
    }
    return [];
  }, [locationBanks, geoLocationBanks, view, locationValue, locationError, suggests, guesses]);

  const guessBanks = useMemo(() => {
    if (onLocationChange) {
      onLocationChange('geo');
    }

    const banks = geoIPBanks.filter((bank) => bank.bankCode && bank.name);
    return guesses ? banks.slice(0, guesses) : banks;
  }, [geoIPBanks, guesses]);

  useEffect(() => {
    getLocationBanks(locationValue).then((banks) => {
      if (banks.length === 0) {
        enqueueSnackbar(t('location.failed'));
      } else if (locationValue) {
        setView('locationView');
      }
    });
  }, [enqueueSnackbar, getLocationBanks, locationValue]);

  const onAutomaticSearch = useCallback(() => {
    setView('geoLocationView');

    getGeolocationBanks()
      .then((banks) => {
        if (banks.length === 0) {
          enqueueSnackbar(t('automatic.failed'));
        } else {
          enqueueSnackbar(t('automatic.info'));
        }
      })
      .catch(() => {
        enqueueSnackbar(t('automatic.failed'));
      });
  }, [enqueueSnackbar, getGeolocationBanks]);

  const hideGuessBanks = locationValue?.length > 0;

  const minHeight = useMemo(() => {
    return guesses > 0 ? { xs: `${(guesses + 1) * 54}px`, lg: `${(guesses + 1) * 74}px` } : 0;
  }, [guesses]);

  return (
    <Stack flexDirection="column" sx={styles.container}>
      <Stack direction="column" gap={6} px={5}>
        <Box
          role={onBack ? 'button' : undefined}
          onClick={onBack}
          display="flex"
          alignItems="center"
          gap={4}
          sx={{ cursor: onBack ? 'pointer' : undefined }}
        >
          {onBack && <ChevronLeft sx={{ fontSize: { xs: '18px', lg: '30px' } }} />}
          {headline && (
            <Typography component="h3" variant="h3" color="text.primary">
              {headline}
            </Typography>
          )}
        </Box>
        {subline && (
          <Typography variant="body2" color="text.secondary">
            {subline}
          </Typography>
        )}
      </Stack>
      <Spacer margin="text" />
      <Grid container spacing={6}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <TextInput aria-controls="location" name="locationName" label="PLZ oder Wohnort" placeholder="" fullWidth />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Button endIcon={<Target />} onClick={onAutomaticSearch} fullWidth={true}>
            Orten
          </Button>
        </Grid>
      </Grid>
      <Spacer margin="text" />
      <Box minHeight={minHeight}>
        <Box
          sx={visuallyHidden}
          aria-live="polite"
          id="search-suggestions-description"
        >{`${banks.length} ${banks.length === 1 ? 'Sparkasse' : 'Sparkassen'} ${banks.length === 1 ? 'wurde' : 'wurden'} gefunden.`}</Box>
        {banks.length > 0 && (
          <CategoryLinkList
            aria-live="polite"
            aria-atomic="true"
            aria-describedby="search-suggestions-description"
            role="listbox"
            id="location"
            md={12}
            lg={12}
            component="ul"
          >
            {banks.map((entry) => {
              return (
                <SparkasseAssignBankButton
                  key={entry.bankCode}
                  bankCode={entry.bankCode}
                  name={entry.name}
                  onSelectBank={onSelectBank}
                  ariaLabel={entry.name}
                  tabIndex={0}
                />
              );
            })}
          </CategoryLinkList>
        )}
        {!hideGuessBanks && guessBanks.length > 0 && (
          <>
            {banks.length > 0 ? <Spacer /> : null}
            <Typography variant="h6" innerPadding="both">
              Vermutung
            </Typography>
            <Divider />
            <Box
              sx={visuallyHidden}
              aria-live="polite"
              id="search-guesss-suggestions-description"
            >{`${guessBanks.length} ${guessBanks.length === 1 ? 'Sparkasse' : 'Sparkassen'} ${guessBanks.length === 1 ? 'wurde' : 'wurden'} gefunden.`}</Box>
            <CategoryLinkList
              aria-live="polite"
              aria-atomic="true"
              aria-describedby="search-guesss-suggestions-description"
              role="listbox"
              id="location"
              component="ul"
              md={12}
              lg={12}
            >
              {guessBanks.map((entry) => {
                return (
                  <SparkasseAssignBankButton
                    key={entry.bankCode}
                    bankCode={entry.bankCode}
                    name={entry.name}
                    onSelectBank={onSelectBank}
                    ariaLabel={entry.name}
                    tabIndex={0}
                  />
                );
              })}
            </CategoryLinkList>
          </>
        )}
      </Box>
    </Stack>
  );
};
